import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import TextArea, { TextAreaRef } from 'antd/es/input/TextArea';
import { RcFile } from 'antd/es/upload';
import { mediaAssetSchema } from '@/entities/media-asset';
import { useNotificationContext } from '@/features/notifications';
import { useFileUpload } from '@/shared/hooks';
import { Row } from '@/shared/ui';
import { MAX_MESSAGE_LENGTH } from '../config/chat';

type ChatInputProps = {
  canUpload?: boolean;
  disabled: boolean;
  isLoading: boolean;
  placeholder?: string;
  sendMessage: (message: string, file?: RcFile) => void;
};

const ChatInput = ({
  canUpload,
  disabled,
  isLoading,
  placeholder,
  sendMessage,
}: ChatInputProps) => {
  const { showNotification } = useNotificationContext();
  const [message, setMessage] = useState('');
  const textAreaRef = useRef<TextAreaRef>(null);
  const { fileList, onBeforeUpload, onAfterUpload, onRemove } = useFileUpload(
    mediaAssetSchema,
    (message, file) => {
      showNotification({
        type: 'error',
        message: `Error uploading file "${file.name}"`,
        description: message,
      });
    },
    false,
  );

  const handleSendMessage = useCallback(() => {
    sendMessage(message, fileList[0]);
    setMessage('');
    onAfterUpload();
  }, [fileList, message, sendMessage, onAfterUpload]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.code === 'Enter' || e.code === 'NumpadEnter') && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  useEffect(() => {
    setTimeout(() => {
      textAreaRef.current?.focus();
    }, 100);
  }, [message]);

  return (
    <>
      <Row>
        <TextArea
          ref={textAreaRef}
          value={message}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(e) => setMessage(e.target.value)}
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ fontSize: 14 }}
          autoFocus
          showCount
        />
        {canUpload && (
          <Upload
            fileList={fileList}
            beforeUpload={onBeforeUpload}
            showUploadList={false}
          >
            <Button
              type="text"
              disabled={disabled}
              icon={<PaperClipOutlined style={{ fontSize: 16 }} />}
              style={{ color: '#bbb' }}
            />
          </Upload>
        )}
        <Button
          type="text"
          disabled={(!message.trim() && !fileList) || isLoading}
          icon={<SendOutlined style={{ fontSize: 16 }} />}
          onClick={handleSendMessage}
          style={{ color: '#bbb' }}
        />
      </Row>
      <Row>
        <Upload fileList={fileList} onRemove={onRemove} />
      </Row>
    </>
  );
};

export default ChatInput;
